import React, { useState } from "react";
import { Form, Input, Select, Button, Upload, Tag, message } from "antd";
import { ArrowLeftOutlined, PaperClipOutlined, InboxOutlined } from "@ant-design/icons";
import { createTicket } from "../../services/supportService";

const { TextArea } = Input;
const { Option } = Select;
const { Dragger } = Upload;

const CATEGORIES = [
  { value: "BOOKING", label: "Booking" },
  { value: "POS", label: "POS" },
  { value: "ACCOUNTS", label: "Accounts" },
  { value: "CHANNEL_MANAGER", label: "Channel Manager" },
  { value: "BOOKING_ENGINE", label: "Booking Engine" },
  { value: "PAYMENT_GATEWAY", label: "Payment Gateway" },
  { value: "OTHERS", label: "Others" },
];

export default function CreateTicketForm({ hotelId, onBack, onCreated }) {
  const [form] = Form.useForm();
  const [attachments, setAttachments] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setAttachments((prev) => [...prev, e.target.result]);
      if (file.type.startsWith("image/")) {
        setPreviews((prev) => [...prev, { name: file.name, url: e.target.result }]);
      } else {
        setPreviews((prev) => [...prev, { name: file.name, url: null }]);
      }
    };
    reader.readAsDataURL(file);
    return false;
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await createTicket(hotelId, { ...values, attachments });
      message.success("Ticket created successfully!");
      form.resetFields();
      setAttachments([]);
      setPreviews([]);
      onCreated();
    } catch {
      message.error("Failed to create ticket. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-500 hover:text-gray-800 mb-2 text-sm">
          <ArrowLeftOutlined /> Back
        </button>
        <div className="font-semibold text-gray-800">Create New Ticket</div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select placeholder="Select issue category">
              {CATEGORIES.map((c) => (
                <Option key={c.value} value={c.value}>{c.label}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: "Please describe the issue" }]}
          >
            <TextArea rows={5} placeholder="Describe the issue in detail..." />
          </Form.Item>

          <Form.Item label="Attachments">
            <Dragger
              multiple
              beforeUpload={handleFile}
              showUploadList={false}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text text-sm">Click or drag files to attach</p>
              <p className="ant-upload-hint text-xs">Images, PDFs, and Office documents supported</p>
            </Dragger>

            {previews.length > 0 && (
              <div className="mt-3 space-y-2">
                {previews.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded p-2">
                    {p.url ? (
                      <img src={p.url} alt={p.name} className="w-12 h-12 object-cover rounded" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                        <PaperClipOutlined className="text-gray-500" />
                      </div>
                    )}
                    <span className="text-xs text-gray-700 flex-1 truncate">{p.name}</span>
                    <Tag
                      closable
                      onClose={() => removeAttachment(i)}
                      className="cursor-pointer"
                    >
                      Remove
                    </Tag>
                  </div>
                ))}
              </div>
            )}
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block>
              Create Ticket
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}
